import { useEffect, useRef, useState } from 'react';
import { restoreAutoSplitState } from '../lib/guestSplitFunnel';

export const RESTORE_VEIL_TIMEOUT_MS = 10000;

/**
 * Dashboard-mount auto-split restore, StrictMode-proof.
 *
 * The previous inline effect used a boolean one-shot ref + a `cancelled` cleanup flag.
 * Under React.StrictMode (dev) the mount→cleanup→remount probe preserved the ref:
 * run #1 set the ref and was then cancelled (result discarded), run #2 was blocked by
 * the ref — so the restored queue was NEVER applied and the modal never reopened after
 * signup. This hook fixes both halves:
 *   - the guard is keyed by user.id and a completed run is what makes it final; a run
 *     that fails releases the guard so a remount can retry;
 *   - there is NO cancelled-discard: restoreAutoSplitState is idempotent (claim has an
 *     already-owner path, revalidation is read-only), and applying the result after a
 *     StrictMode probe-remount is exactly what we want. On a genuine unmount React
 *     no-ops the state update.
 *
 * Callbacks are kept in a ref so their identity never re-triggers the effect; only a
 * user change (login/logout/conversion) can start a new restore.
 */
export function useAutoSplitRestore(user, api, callbacks, storage) {
  const cbRef = useRef(callbacks);
  cbRef.current = callbacks;
  const restoredForUserRef = useRef(null);
  // 'idle' (no user yet) → 'in_flight' → 'done' | 'error'. Drives the funnel veil.
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (!user || restoredForUserRef.current === user.id) return;
    restoredForUserRef.current = user.id;
    setStatus('in_flight');
    (async () => {
      try {
        const { queue, resumeMethod, expired } = await restoreAutoSplitState(api, user, storage);
        if (expired && cbRef.current.onExpired) cbRef.current.onExpired();
        if (queue.length > 0 && cbRef.current.onRestored) cbRef.current.onRestored(queue, resumeMethod);
        setStatus('done');
      } catch {
        // Best-effort: release the guard so a remount / user re-hydration can retry.
        restoredForUserRef.current = null;
        setStatus('error');
      }
    })();
  }, [user, api, storage]);

  return status;
}

/**
 * Funnel-arrival veil (interaction gap fix): fullscreen overlay visibility while the
 * post-signup restore is in flight. Returns true ONLY when the user arrived from the
 * guest funnel (flag read by the caller) AND the restore hasn't resolved yet. A 10s
 * safety timeout force-hides it — never an indefinitely blocked screen. `onGiveUp`
 * fires at most once when the veil comes down WITHOUT a visible outcome (restore
 * error, timeout, or done-with-nothing — e.g. a swallowed network failure where the
 * funnel flag survives for retry) so the caller can toast "reload to retry".
 */
export function useGuestRestoreVeil(fromFunnelArrival, status, restoredSomething, onGiveUp) {
  const [timedOut, setTimedOut] = useState(false);
  const gaveUpRef = useRef(false);
  const onGiveUpRef = useRef(onGiveUp);
  onGiveUpRef.current = onGiveUp;
  // restoredSomething is held in a ref, NOT a dependency: the give-up decision is latched to
  // the restore RESOLUTION (the status / timeout transition below), reading whatever value is
  // current AT THAT MOMENT. After the modal shows (restoredSomething was true at 'done'), the
  // user emptying the queue (close / discard / process / save) flips it false again, but that
  // must NOT be re-read as "the restore recovered nothing" — that was the false-give-up bug.
  // fromFunnelArrival stays true for the whole session, so without this latch any later
  // queue-empty would fire the red toast.
  const restoredSomethingRef = useRef(restoredSomething);
  restoredSomethingRef.current = restoredSomething;

  useEffect(() => {
    if (!fromFunnelArrival || status !== 'in_flight' || timedOut) return;
    const timer = setTimeout(() => setTimedOut(true), RESTORE_VEIL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [fromFunnelArrival, status, timedOut]);

  // Fires once, only on a restore-resolution transition (status → done/error, or timeout).
  // restoredSomething is read from the ref so later queue changes don't re-trigger this.
  useEffect(() => {
    if (!fromFunnelArrival || gaveUpRef.current) return;
    const failed = status === 'error'
      || (timedOut && status === 'in_flight')
      || (status === 'done' && !restoredSomethingRef.current);
    if (failed) {
      gaveUpRef.current = true;
      if (onGiveUpRef.current) onGiveUpRef.current();
    }
  }, [fromFunnelArrival, status, timedOut]);

  return fromFunnelArrival && status === 'in_flight' && !timedOut;
}
